import { useEffect, useRef } from 'react';
import { showToast } from '../components/Toast';
import {
  collectNewInboxToasts,
  formatInboxToast,
  isElectronDesktop,
  openInboxTarget,
  showDesktopToast,
} from '../utils/desktopNotify';

async function windowIsFocused() {
  if (isElectronDesktop() && typeof window.electronAPI.notify?.isWindowFocused === 'function') {
    try {
      return await window.electronAPI.notify.isWindowFocused();
    } catch {
      return document.hasFocus();
    }
  }
  return typeof document !== 'undefined' && document.hasFocus();
}

/**
 * New unread inbox rows announce themselves.
 * Focused: an in-app toast. Background: a Windows toast.
 * First snapshot never fires. Click opens the mission.
 */
export function useDesktopInboxToasts(items, markRead) {
  const prevIdsRef = useRef(null);

  useEffect(() => {
    const { nextIds, toasts } = collectNewInboxToasts(prevIdsRef.current, items);
    prevIdsRef.current = nextIds;
    if (!toasts.length) return undefined;
    let cancelled = false;
    (async () => {
      const focused = await windowIsFocused();
      if (cancelled) return;
      toasts.forEach((item) => {
        if (focused) {
          const { title, message } = formatInboxToast(item);
          showToast(message, {
            type: 'messenger',
            title,
            duration: 8000,
            actionHint: 'Click to open',
            onClick: () => {
              markRead?.(item.id);
              openInboxTarget(item);
            },
          });
          return;
        }
        showDesktopToast(item);
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [items, markRead]);

  useEffect(() => {
    if (!isElectronDesktop() || typeof window.electronAPI.notify?.onClicked !== 'function') {
      return undefined;
    }
    return window.electronAPI.notify.onClicked((payload) => {
      if (payload?.id) markRead?.(payload.id);
      openInboxTarget(payload);
    });
  }, [markRead]);
}
